import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import "./PaymentSuccess.css";
import { HelenService } from "../../services/helen/helenService";
import Loader from "../../components/common/Loader/Loader";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState("Loading subscription details...");
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null); // null until subscription is fetched

  const redirectTimeout = 10; // seconds

  const handleFetchSubscription = useCallback(async () => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setMessage("Invalid payment session.");
      return;
    }

    setLoading(true);
    try {
      const response = await HelenService.getSubscription(sessionId);

      if (response.status === 200 && response.data) {
        setMessage("Payment successful! Your subscription is active. 🎉");
        setSubscription(response.data.subscription);
        // Start countdown
        setCountdown(redirectTimeout);
      } else {
        setMessage(
          "Payment successful, but subscription details could not be retrieved."
        );
      }
    } catch (error) {
      setMessage("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    handleFetchSubscription();
  }, [handleFetchSubscription]);

  // Countdown + redirect logic
  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      navigate("/admin");
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  const redirectToDashboard = () => {
    navigate("/admin");
  };

  if (loading) return <Loader />;

  return (
    <div className="page-container">
      <div className="subscription-card">
        <h2 className="subscription-message">{message}</h2>

        {subscription && (
          <div className="subscription-details">
            <h3 className="details-title">Subscription Details</h3>

            <div className="details-row">
              <span className="label">Plan ID:</span>
              <span>{subscription.plan_id}</span>
            </div>

            <div className="details-row">
              <span className="label">Status:</span>
              <span
                className={`subscription-status-badge ${
                  subscription.is_active ? "active" : "inactive"
                }`}
              >
                {subscription.is_active ? "Active" : "Inactive"}
              </span>
            </div>

            <div className="details-row">
              <span className="label">Start Date:</span>
              <span>
                {subscription.start_date
                  ? new Date(subscription.start_date).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>

            <div className="details-row">
              <span className="label">End Date:</span>
              <span>
                {subscription.end_date
                  ? new Date(subscription.end_date).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>

            {countdown !== null && (
              <p className="redirect-text">
                Redirecting in <strong>{countdown}</strong> seconds...
              </p>
            )}
            <button className="redirect-button" onClick={redirectToDashboard}>
              Go to Dashboard Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
